/**
 * Chart utility functions and common configurations
 */

// Common color palettes
export const chartColors = {
  primary: '#3B82F6', // blue-500
  success: '#10B981', // emerald-500
  warning: '#F59E0B', // amber-500
  danger: '#EF4444', // red-500
  info: '#06B6D4', // cyan-500
  secondary: '#6B7280', // gray-500
  purple: '#8B5CF6', // violet-500
  pink: '#EC4899', // pink-500
  teal: '#14B8A6', // teal-500
};

export const colorPalette = [
  chartColors.primary,
  chartColors.success,
  chartColors.warning,
  chartColors.danger,
  chartColors.purple,
  chartColors.pink,
  chartColors.info,
  chartColors.teal,
];

// Generate gradient color
export const getGradientColor = (color: string, opacity: number = 0.1) => {
  return {
    type: 'linear',
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      {
        offset: 0,
        color: color,
      },
      {
        offset: 1,
        color: `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`,
      },
    ],
  };
};

// Format number with K/M/B suffixes
export const formatNumber = (value: number): string => {
  if (value >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
  return value.toString();
};

// Common chart themes
export const chartTheme = {
  title: {
    fontSize: 16,
    fontWeight: 'bold' as const,
    color: '#374151',
  },
  legend: {
    textStyle: {
      color: '#6b7280',
    },
  },
  tooltip: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderColor: '#e5e7eb',
    borderWidth: 1,
    textStyle: {
      color: '#374151',
    },
  },
  axis: {
    axisLine: {
      lineStyle: {
        color: '#e5e7eb',
      },
    },
    axisLabel: {
      color: '#6b7280',
      fontSize: 12,
    },
    splitLine: {
      lineStyle: {
        color: '#f3f4f6',
      },
    },
  },
};

// Animation configurations
export const chartAnimations = {
  fast: {
    animationDuration: 500,
    animationEasing: 'cubicOut',
  },
  normal: {
    animationDuration: 1000,
    animationEasing: 'cubicOut',
  },
  slow: {
    animationDuration: 1500,
    animationEasing: 'cubicOut',
  },
  elastic: {
    animationDuration: 1000,
    animationEasing: 'elasticOut',
  },
};

// Common status colors
export const statusColors = {
  active: chartColors.success,
  inactive: chartColors.secondary,
  pending: chartColors.warning,
  error: chartColors.danger,
  completed: chartColors.primary,
};

// Generate mock data for testing
export const generateMockData = (count: number, min: number = 0, max: number = 100): number[] => {
  return Array.from({ length: count }, () => Math.floor(Math.random() * (max - min + 1)) + min);
};

// Format date labels
export const getMonthLabels = (count: number = 12): string[] => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months.slice(0, count);
};

export const getDayLabels = (count: number = 7): string[] => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return days.slice(0, count);
};